import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import i18n, { LOCALE_STORAGE_KEY } from '../i18n';


type LocaleContextValue = {
  locale: string,
  setLocale: (locale: string) => void
};

// Mirrors UweStudioFrontend's LocaleContext: a tiny provider that persists the
// chosen locale to localStorage and drives both i18next and dayjs. This is what
// makes the language choice survive reloads (the previous Lingui setup did not
// persist, so every reload reset to the default language).
const LocaleContext = createContext<LocaleContextValue>(undefined as unknown as LocaleContextValue);

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState(() => (i18n.language === 'es' ? 'es' : 'en'));

  const setLocale = useCallback((newLocale: string) => {
    const next = newLocale === 'es' ? 'es' : 'en';
    setLocaleState(next);
    localStorage.setItem(LOCALE_STORAGE_KEY, next);
    i18n.changeLanguage(next);
    dayjs.locale(next);
  }, [setLocaleState, i18n, dayjs]);

  const value = useMemo(() => ({locale, setLocale}), [locale, setLocale])

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used within a LocaleProvider');
  return ctx;
}

export const LANGS = {
  en: { name: 'English' },
  es: { name: 'Español' },
};

export const LOCALES = Object.keys(LANGS)
